import {
  GetMyUpcomingReservationsResponseDto,
  ReservationQuestionDto,
  ReservationQuestionOptionDto,
} from "@/api/generated/generatedTypes";
import { EventQuestionType } from "@/api/reservation/dto";
import { QuestionnaireUILayout } from "@/components/shared/QuestionnaireUILayout";
import { EventReservationFormValues } from "@/pages/EventReservation.page";
import { Button, Center, Text } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";
import { useTimeout } from "@mantine/hooks";
import { useState } from "react";
import { DinnerQuestionStep, getQuestionIndexForStep } from "../constants";
import { EventQuestionAndAnswerResolver } from "../EventQuestionAndAnswerResolver";
import { calculateOverviewProgressValue, calculateProgressValue } from "../progressUtils";
import { getPathForReservationQuestion } from "../utils";
import { ReturningUserOverview } from "./ReturningUserOverview";

interface DinnerQuestionnaireProps {
  eventQuestions: ReservationQuestionDto[];
  form: UseFormReturnType<
    EventReservationFormValues,
    (values: EventReservationFormValues) => EventReservationFormValues
  >;
  onQuestionnaireComplete: () => void;
  onBackToDateLocation?: () => void;
  latestReservation: GetMyUpcomingReservationsResponseDto | null;
  totalSteps: number;
  stepOffset: number;
}

export function DinnerQuestionnaire({
  eventQuestions,
  form,
  onQuestionnaireComplete,
  onBackToDateLocation,
  latestReservation,
  totalSteps,
  stepOffset,
}: DinnerQuestionnaireProps) {
  const isReturningUser = Boolean(latestReservation);
  const [showOverview, setShowOverview] = useState(isReturningUser);
  const [currentStep, setCurrentStep] = useState(0);

  const maxSteps = eventQuestions.length;
  const isLastStep = currentStep === maxSteps - 1;
  const currentQuestion = eventQuestions[currentStep];
  const progressValue = showOverview
    ? calculateOverviewProgressValue(stepOffset, totalSteps)
    : calculateProgressValue(currentStep, stepOffset, totalSteps);

  const handleCategoryClick = (step: DinnerQuestionStep) => {
    const questionIndex = getQuestionIndexForStep(step, eventQuestions);
    if (questionIndex !== -1) {
      setShowOverview(false);
      setCurrentStep(questionIndex);
    }
  };

  const goToNextStep = () => {
    if (!isLastStep) {
      setCurrentStep((s) => s + 1);
    }
  };

  const { start: goToNextStepAfterDelay, clear: clearGoToNextStepDelay } = useTimeout(
    goToNextStep,
    300
  );

  const goToPreviousStep = () => {
    clearGoToNextStepDelay();

    if (showOverview) {
      // At overview - go back to date/location phase
      if (onBackToDateLocation) {
        onBackToDateLocation();
      }
      // At first question
    } else if (currentStep === 0) {
      if (isReturningUser) {
        // Returning users go back to overview
        setShowOverview(true);
      } else {
        // New users go back to date/location phase
        if (onBackToDateLocation) {
          onBackToDateLocation();
        }
      }
    } else if (currentStep > 0) {
      // Not at first question - go to previous question
      setCurrentStep((s) => s - 1);
    }
  };

  const handleBackToOverview = () => {
    setShowOverview(true);
    setCurrentStep(0);
  };

  const handleSelectSingle = (option: ReservationQuestionOptionDto) => {
    if (currentQuestion) {
      const path = getPathForReservationQuestion(currentQuestion);
      if (path) {
        form.setFieldValue(path as any, option.value);
      }

      if (currentQuestion.type === EventQuestionType.SELECT_SINGLE) {
        goToNextStepAfterDelay();
      }
    }
  };

  const handleSelectMultiple = (option: ReservationQuestionOptionDto) => {
    if (currentQuestion && currentQuestion.type === EventQuestionType.SELECT_MULTIPLE) {
      const path = getPathForReservationQuestion(currentQuestion);
      const currentVal = (form.getInputProps(path as any).value as string[] | null) || [];
      const isRemoving = currentVal.includes(option.value);
      const newSelection = isRemoving
        ? currentVal.filter((val) => val !== option.value)
        : [...currentVal, option.value];
      form.setFieldValue(path as any, newSelection);

      if (isRemoving && option.customInputType === "text_input") {
        form.setFieldValue(customInputPath, "");
      }
    }
  };

  const getIsNextButtonDisabled = (): boolean => {
    if (!currentQuestion) return true;
    const path = getPathForReservationQuestion(currentQuestion);
    const value = form.getInputProps(path as any).value;
    if (currentQuestion.type === EventQuestionType.TEXT) {
      return !value;
    }
    if (currentQuestion.type === EventQuestionType.SELECT_SINGLE) {
      return value === null || value === undefined;
    }
    if (currentQuestion.type === EventQuestionType.SELECT_MULTIPLE) {
      const hasValues = value && (value as string[]).length > 0;
      if (!hasValues) return true;

      const hasCustomInputOption = currentQuestion.options?.some(
        (opt) => opt.customInputType === "text_input" && (value as string[]).includes(opt.value)
      );

      if (hasCustomInputOption) {
        const customText = form.getInputProps(customInputPath).value;
        return !customText || customText.trim().length === 0;
      }

      return false;
    }
    return false;
  };

  // Show overview for returning users
  if (showOverview && isReturningUser && latestReservation) {
    const overviewContent = (
      <ReturningUserOverview
        latestReservation={latestReservation}
        onCategoryClick={handleCategoryClick}
        form={form}
      />
    );

    return (
      <QuestionnaireUILayout
        progressValue={progressValue}
        showBackButton={!!onBackToDateLocation}
        onBackButtonClick={onBackToDateLocation || (() => {})}
        title="저녁 식사 취향"
        subtitle="이번 저녁 식사, 특별히 고려해야 할 부분 있을까요?"
        questionAreaContent={overviewContent}
        showNextButton={false}
        onNextButtonClick={() => {}}
        isNextButtonDisabled={false}
        isNextButtonLoading={false}
        showSubmitButton={true}
        onSubmitButtonClick={onQuestionnaireComplete}
        isSubmitButtonDisabled={false}
        submitButtonText="계속하기"
        pbForQuestionTitle="xl"
      />
    );
  }

  if (!currentQuestion) {
    // This check is important if eventQuestions could be empty or currentStep is out of bounds
    return (
      <Center h="100%">
        <Text>Error: Question not found for current step.</Text>
      </Center>
    );
  }

  const showNextButton =
    !isLastStep &&
    (currentQuestion.type === EventQuestionType.TEXT ||
      currentQuestion.type === EventQuestionType.SELECT_MULTIPLE);

  const customInputPath = "dinnerPreference.dietaryRestrictionsOtherText" as const;
  const customInputValue = form.getInputProps(customInputPath).value;
  const handleCustomInputChange = (value: string) => {
    form.setFieldValue(customInputPath, value);
  };

  const questionArea = (
    <EventQuestionAndAnswerResolver
      currentQuestion={currentQuestion}
      form={form}
      onSelectSingle={handleSelectSingle}
      onSelectMultiple={handleSelectMultiple}
      customInputValue={customInputValue}
      onCustomInputChange={handleCustomInputChange}
    />
  );

  // "Back to Overview" button for returning users
  const backToOverviewButton =
    isReturningUser && !showOverview ? (
      <Button
        variant="subtle"
        size="sm"
        onClick={handleBackToOverview}
        styles={{
          root: {
            color: "white",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
          },
        }}
      >
        ← Overview 로 돌아가기
      </Button>
    ) : undefined;

  return (
    <QuestionnaireUILayout
      progressValue={progressValue}
      showBackButton={true}
      onBackButtonClick={goToPreviousStep}
      title={currentQuestion.title}
      subtitle={currentQuestion.subtitle}
      questionAreaContent={questionArea}
      showNextButton={showNextButton}
      onNextButtonClick={goToNextStep}
      isNextButtonDisabled={getIsNextButtonDisabled()}
      isNextButtonLoading={false}
      showSubmitButton={isLastStep}
      onSubmitButtonClick={onQuestionnaireComplete}
      isSubmitButtonDisabled={getIsNextButtonDisabled()}
      isErrorState={false}
      pbForQuestionTitle={Boolean(currentQuestion?.options?.length ?? 0 > 6) ? "sm" : "xl"}
      submitButtonText="다음"
      topActionButton={backToOverviewButton}
    />
  );
}
